<template>
  <tooltip
    :content="tooltipContent"
    :position="'top'"
    :fallback-placements="['bottom-end']"
    :full-width="true"
    :delay="250"
    :hide="!tooltipFn"
  >
    <template #body>
      <div
        :class="{
          [$style.table_cell]: true,
          [$style.right_border]: skin === 'horizontal-borders' && !isLastColumn && !isGroupRow,
        }"
        :style="cellStyles"
      >
        <!-- GROUP EXPAND DROP ICON -->
        <div
          v-if="isGroupCell"
          :class="$style.drop_icon"
        >
          <svg-sprite
            :name="isRowExpanded ? 'drop-down' : 'drop-right'"
            type="bold"
            color="#999999"
            :size="20"
          />
        </div>

        <!-- RESOURCES CELL -->
        <resource-cell
          v-if="cell.column.columnDef.template === 'resource'"
          :cell="cell"
          :columns-sizes="columnsSizes"
        />

        <!-- PROJECT CELL -->
        <project-cell
          v-if="cell.column.columnDef.template === 'project'"
          :cell="cell"
          :columns-sizes="columnsSizes"
        />

        <!-- STATUS CELL -->
        <status-cell
          v-if="isStatusCell"
          :cell="cell"
        />

        <!-- PROGRESS CELL -->
        <progress-cell
          v-if="isProgressCell"
          :cell="cell"
        />

        <!-- CELL AND AGGREGATION CELL-->
        <div
          v-if="withoutTemplateCell"
          :class="$style.text_cell"
          :style="{maxWidth: cellValueWidth}"
        >
          <div
            v-if="isAggregationCell"
            :class="$style.sum_aggregation_label"
          >
            {{ locale('common_sum') }}
          </div>
          <svg-sprite
            v-if="isGroupCell && cell.column.columnDef.icon"
            :name="cell.column.columnDef.icon.name"
            :type="cell.column.columnDef.icon.type"
            :color="cell.column.columnDef.icon.color"
            :size="cell.column.columnDef.icon.size"
            :style="{marginRight: '4px', minWidth: cell.column.columnDef.icon.size + 'px'}"
          />
          <div
            :class="{
              [$style.value]: true,
              [$style.align_right]: isAlignRight,
              [$style.text_bold]: isGroupRow,
              [$style.full_width]: !isAlignRight,
              [$style.is_negative]: isNegativeValue,
            }"
          >
            {{ cellValue }}
          </div>
        </div>

        <!-- GROUP SUBROWS COUNT -->
        <span
          v-if="isGroupCell"
          :class="$style.subrows_counter"
        >{{ row.subRows.length }}</span>
      </div>
    </template>
  </tooltip>
</template>

<script>
import tooltip from '../../../common/VueComponents/tooltips/tooltip.vue';
import projectCell from './cellProject.vue';
import resourceCell from './cellResource.vue';
import statusCell from './cellStatus.vue';
import progressCell from './cellProgress.vue';

export default {
  components: {
    projectCell, resourceCell, statusCell, progressCell, tooltip,
  },
  props: {
    row: { type: Object, required: true },
    cell: { type: Object, required: true },
    columnsSizes: { type: Object, required: true, default: () => {} },
    skin: { type: String, required: false, default: '' },
    isLastColumn: { type: Boolean, required: false, default: false },
    isRowExpanded: { type: Boolean, required: false },
  },
  data() {
    return {
      locale: __,
    };
  },
  computed: {
    cellValueWidth() {
      if (this.isGroupCell) {
        return 'fit-content';
      }

      const width = this.columnsSizes[this.cell.column.id];

      return `${parseFloat(width) - 10}px`;
    },
    isGroupRow() {
      return this.row.getIsGrouped();
    },
    isGroupCell() {
      return this.cell.getIsGrouped();
    },
    withoutTemplateCell() {
      return !this.cell.column.columnDef.template;
    },
    isStatusCell() {
      return this.cell.column.columnDef.template === 'status';
    },
    isProgressCell() {
      return this.cell.column.columnDef.template === 'progress';
    },
    cellStyles() {
      const firstColumn = Object.keys(this.columnsSizes)[0];

      const paddings = {
        group: { padding: '6px 8px' },
        normal: { padding: '8px 12px' },
        statusCell: { padding: '8px 12px 8px 6px' },
        progressCell: { padding: '8px 12px 8px 6px' },
      };
      const paddingLeftStep = 20;

      if (this.isGroupCell) {
        return {
          ...paddings.group,
          paddingLeft: `${8 + (paddingLeftStep * this.row.depth)}px`,
        };
      }

      if (!this.isGroupRow && this.cell.column.id === firstColumn) {
        if (this.row.depth >= 5) {
          return {
            ...paddings.normal,
            paddingLeft: `${12 + (paddingLeftStep * 5)}px`,
          };
        }

        return {
          ...paddings.normal,
          paddingLeft: `${12 + (paddingLeftStep * this.row.depth)}px`,
        };
      }

      if (this.isStatusCell) {
        return paddings.statusCell;
      }

      if (this.isProgressCell) {
        return paddings.progressCell;
      }

      return paddings.normal;
    },
    isAlignRight() {
      if (this.cell.column.columnDef.align === 'right') {
        return true;
      }

      return false;
    },
    isAggregationCell() {
      return !this.isGroupCell && this.isGroupRow && this.isAlignRight;
    },
    cellValue() {
      return this.cell.column.columnDef.cell ? this.cell.column.columnDef.cell(this.cell.getContext()) : this.cell.getValue();
    },
    isNegativeValue() {
      const value = this.cell.getValue();

      if (value < 0) {
        return true;
      }

      return false;
    },
    tooltipFn() {
      const tooltipContentFn = this.cell.column.columnDef.tooltipContent;

      return tooltipContentFn || null;
    },
    tooltipContent() {
      if (this.tooltipFn) {
        return this.tooltipFn(this.row);
      }

      return '';
    },
  },
};
</script>

<style module src="./cell.less" lang="less"></style>
